import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, CircleCheckIcon, EyeIcon } from 'lucide-react';
import { pageSamples } from '../data/content';
import { CtaButton } from './CtaButton';
const proofChips = ['Format A4', 'Gotowe do druku', 'Z kluczem odpowiedzi'];
const AUTOPLAY_MS = 6000;
export function InsidePages() {
  const [index, setIndex] = useState<number>(0);
  const [direction, setDirection] = useState<number>(1);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const reduceMotion = useReducedMotion();
  const total = pageSamples.length;
  const current = pageSamples[index];
  const go = (next: number) => {
    setDirection(next > index ? 1 : -1);
    setIndex((next + total) % total);
  };
  useEffect(() => {
    if (isPaused) return;
    const id = window.setTimeout(() => {
      setDirection(1);
      setIndex(prev => (prev + 1) % total);
    }, AUTOPLAY_MS);
    return () => window.clearTimeout(id);
  }, [index, isPaused, total]);
  const slideOffset = reduceMotion ? 0 : direction * 28;
  return <section id="inside-pages" className="w-full overflow-x-hidden bg-ink px-5 py-20 sm:py-28" data-id="element-130" data-mp="1rk4f6lt5z6rv">
      <div className="mx-auto max-w-5xl" data-id="element-131" data-mp="1dtsjkcxdepvz">
        <div className="mx-auto max-w-2xl text-center" data-id="element-132" data-mp="1hvdzk757bkae">
          <span className="inline-flex items-center gap-2 rounded-full bg-brand px-4 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.16em] text-white" data-id="element-133" data-mp="1htta2vxqurje">
            <EyeIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-134" data-mp="18v7pywcmj4gi" />
            Podgląd prawdziwych stron
          </span>
          <h2 className="mt-6 font-display text-[1.9rem] font-black leading-[1.05] tracking-tight text-cream sm:text-[2.8rem]" data-id="element-135" data-mp="uwhshflw72rm">
            Zobacz, co dokładnie{' '}
            <span className="italic text-brand" data-id="element-136" data-mp="2p8o9ww5dqfsd">dostajesz.</span>
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-cream/60 sm:text-base" data-id="element-137" data-mp="2tasvr8pl684g">
            Żadnych zapowiedzi — to autentyczne strony z pakietu. Od ćwiczeń o
            rytmie dla klasy 4 po karty historii muzyki i krzyżówki powtórkowe.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-8 rounded-3xl outline-none focus-visible:ring-4 focus-visible:ring-brand/40 md:mt-14 md:grid-cols-[minmax(0,1fr)_18rem] md:items-center md:gap-10" role="group" aria-roledescription="karuzela" aria-label="Przykładowe strony materiałów" tabIndex={0} onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} onFocus={() => setIsPaused(true)} onBlur={() => setIsPaused(false)} onKeyDown={event => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          go(index + 1);
        }
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          go(index - 1);
        }
      }} data-id="element-138" data-mp="11xrqbpcwj9o">
          <div className="relative min-w-0" data-id="element-139" data-mp="1b1s4zsby91l1">
            <div className="relative flex h-[23rem] items-center justify-center overflow-hidden rounded-3xl border-4 border-brand bg-white p-3 shadow-soft sm:h-[32rem] sm:p-6" data-id="element-140" data-mp="1b2tjk6s6c67i">
              <AnimatePresence initial={false} mode="wait" data-id="element-141" data-mp="1kyd8g98bm4mr">
                <motion.img key={current.src} src={current.src} alt={`${current.title} — przykładowa strona, ${current.level}`} initial={{
                opacity: 0,
                x: slideOffset
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -slideOffset
              }} transition={{
                duration: 0.25,
                ease: [0.23, 1, 0.32, 1]
              }} className="h-full w-full object-contain" data-id="element-142" data-mp="1p11hagx1bbjq" />
              </AnimatePresence>

              <span className="absolute bottom-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-ink/85 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-cream" data-id="element-143" data-mp="2crnhjm0srrj6">
                Strona {index + 1} z {total}
              </span>
            </div>

            <button type="button" onClick={() => go(index - 1)} aria-label="Poprzednia strona" className="absolute left-2 top-1/2 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-brand text-white shadow-soft ring-4 ring-ink/40 transition-[transform,background-color] duration-150 ease-out hover:bg-brand-dark active:scale-95 focus:outline-none focus-visible:ring-brand/40 sm:-left-7 sm:h-16 sm:w-16 sm:ring-0" data-id="element-144" data-mp="201ms4vekmi77">
              <ChevronLeftIcon className="h-7 w-7 sm:h-8 sm:w-8" aria-hidden="true" data-id="element-145" data-mp="1v2jzkafs24g4" />
            </button>
            <button type="button" onClick={() => go(index + 1)} aria-label="Następna strona" className="absolute right-2 top-1/2 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-brand text-white shadow-soft ring-4 ring-ink/40 transition-[transform,background-color] duration-150 ease-out hover:bg-brand-dark active:scale-95 focus:outline-none focus-visible:ring-brand/40 sm:-right-7 sm:h-16 sm:w-16 sm:ring-0" data-id="element-146" data-mp="1q5a4ae5piykd">
              <ChevronRightIcon className="h-7 w-7 sm:h-8 sm:w-8" aria-hidden="true" data-id="element-147" data-mp="j8zte7l5qirs" />
            </button>
          </div>

          <div className="flex min-w-0 flex-col" data-id="element-148" data-mp="16zdo65v4mvq2">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-brand" data-id="element-149" data-mp="1b0p5lo3ukan6">
              {current.level}
            </p>
            <h3 className="mt-2 font-display text-xl font-bold leading-snug text-cream sm:text-2xl" data-id="element-150" data-mp="x7zex6ddihjq">
              {current.title}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-cream/60" data-id="element-151" data-mp="1pxg47v5p5ibq">
              {current.caption}
            </p>

            <ul className="mt-5 space-y-2" data-id="element-152" data-mp="1uoodmr7113ow">
              {proofChips.map(chip => <li key={chip} className="flex items-center gap-2 text-xs font-semibold text-cream/80" data-id="element-153" data-mp="24jvguia7a4gn">
                  <CircleCheckIcon className="h-4 w-4 shrink-0 text-brand" aria-hidden="true" data-id="element-154" data-mp="1zlty5ygos75p" />
                  {chip}
                </li>)}
            </ul>

            <ul className="mt-6 flex flex-wrap gap-2.5" data-id="element-155" data-mp="1ll0y6y7m8jv7">
              {pageSamples.map((page, pageIndex) => <li key={page.src} data-id="element-156" data-mp="1gu10vrul3zs6" data-mpi={pageIndex}>
                  <button type="button" onClick={() => go(pageIndex)} aria-label={`Przejdź do: ${page.title}`} aria-current={pageIndex === index} className={`h-16 w-12 overflow-hidden rounded-lg border-2 bg-white transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-4 focus-visible:ring-brand/40 sm:h-20 sm:w-16 ${pageIndex === index ? 'border-brand' : 'border-cream/20 hover:border-cream/50'}`} data-id="element-157" data-mp="1gszrdrt7vlck">
                    <img src={page.src} alt="" className="h-full w-full object-cover object-top" data-id="element-158" data-mp="3dmhthru1ja21" />
                  </button>
                </li>)}
            </ul>
          </div>
        </div>

        <div className="mt-12 text-center" data-id="element-159" data-mp="2pt82c9nvgghf">
          <p className="font-display text-lg font-bold italic text-cream sm:text-xl" data-id="element-160" data-mp="2utdwbwhm6f2o">
            Takich stron w pakiecie jest ponad 500.
          </p>
          <CtaButton href="#offer" label="Chcę zobaczyć całą ofertę" data-id="element-161" data-mp="2zrfhcmyloogs" />
        </div>
      </div>
    </section>;
}